import { Shield, Sparkles, CheckCircle2 } from "lucide-react";

import BillingSummary from "../review/BillingSummary";
import CompanyInfoCard from "../review/CompanyInfoCard";
import SelectedModulesReview from "../review/SelectedModulesReview";

const StepReviewSummary = ({
  formData,
  modules = [],
  loading = false,
  handleSubmit,
}) => {
  const selectedBaseModules = formData?.SUBSCRIBED_BASEMODULE_DETAILS || [];

  // =====================================================
  // TOTAL CALCULATION
  // =====================================================

  const totalCost = selectedBaseModules.reduce((total, base) => {
    const basePrice = Number(base?.CUSTOM_PRICE || 0);

    const subModulePrice =
      base?.SUBSCRIBED_MODULE_DETAILS?.reduce((moduleTotal, module) => {
        const subTotal =
          module?.SUBSCRIBED_SUBMODULE_DETAILS?.reduce(
            (sum, sub) => sum + Number(sub?.CUSTOM_PRICE || 0),
            0,
          ) || 0;

        return moduleTotal + subTotal;
      }, 0) || 0;

    return total + basePrice + subModulePrice;
  }, 0);

  return (
    <div className="space-y-6">
      {/* ===================================================== */}
      {/* HEADER */}
      {/* ===================================================== */}

      <div
        className="
          relative overflow-hidden
          rounded-3xl
          border border-slate-200 dark:border-slate-800
          bg-white dark:bg-slate-900
          p-6
          shadow-sm
        "
      >
        {/* GLOW */}
        <div
          className="
            absolute top-0 right-0
            w-56 h-56 rounded-full
            bg-gradient-to-br
            from-teal-500/10 to-violet-500/10
            blur-3xl
          "
        />

        <div className="relative z-10 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          {/* LEFT */}
          <div className="flex-1">
            <div className="flex items-start gap-4">
              <div
                className="
                  w-12 h-12 rounded-2xl
                  flex items-center justify-center
                  bg-gradient-to-br
                  from-teal-500 to-emerald-500
                  text-white
                  shadow-lg shadow-teal-500/20
                  shrink-0
                "
              >
                <Sparkles className="w-5 h-5" />
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-800 dark:text-white">
                  Review & Confirm
                </h2>

                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  Verify company information, selected modules and pricing
                  before submission.
                </p>

                {/* TAGS */}
                <div className="flex flex-wrap gap-2 mt-4">
                  <div
                    className="
                      flex items-center gap-2
                      px-3 py-1.5 rounded-xl
                      bg-emerald-50 dark:bg-emerald-500/10
                      text-emerald-700 dark:text-emerald-300
                      text-xs font-medium
                    "
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Ready for Submission
                  </div>

                  <div
                    className="
                      px-3 py-1.5 rounded-xl
                      bg-violet-50 dark:bg-violet-500/10
                      text-violet-700 dark:text-violet-300
                      text-xs font-medium
                    "
                  >
                    {selectedBaseModules.length} Base Modules Selected
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* TOTAL CARD */}
          <div
            className="
              min-w-[240px]
              rounded-3xl
              bg-gradient-to-r
              from-teal-500 to-emerald-500
              p-5
              text-white
              shadow-lg shadow-teal-500/20
            "
          >
            <p className="text-xs font-medium text-white/80 uppercase tracking-wide">
              Estimated Monthly Cost
            </p>

            <h3 className="text-4xl font-bold mt-2">${totalCost.toFixed(2)}</h3>

            <div className="mt-4 pt-4 border-t border-white/20">
              <div className="flex items-center justify-between text-sm">
                <span className="text-white/80">Modules</span>

                <span className="font-semibold">
                  {selectedBaseModules.length}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===================================================== */}
      {/* CONTENT */}
      {/* ===================================================== */}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* LEFT SIDE */}
        <div className="xl:col-span-2 space-y-6">
          <CompanyInfoCard formData={formData} />

          <SelectedModulesReview
            modules={modules}
            selectedBaseModules={selectedBaseModules}
          />
        </div>

        {/* RIGHT SIDE */}
        <div className="space-y-6 xl:sticky xl:top-6 h-fit">
          <BillingSummary
            modules={modules}
            selectedBaseModules={selectedBaseModules}
          />

          {/* INFO CARD */}
          <div
            className="
              rounded-3xl
              border border-blue-200 dark:border-blue-500/20
              bg-blue-50 dark:bg-blue-500/5
              p-5
            "
          >
            <div className="flex items-start gap-4">
              <div
                className="
                  w-11 h-11 rounded-2xl
                  flex items-center justify-center
                  bg-blue-100 dark:bg-blue-500/10
                  shrink-0
                "
              >
                <Shield className="w-5 h-5 text-blue-600 dark:text-blue-300" />
              </div>

              <div>
                <h4 className="text-sm font-semibold text-slate-800 dark:text-white">
                  What happens next?
                </h4>

                <p className="text-xs leading-relaxed mt-2 text-slate-600 dark:text-slate-400">
                  After submission, your company setup will be reviewed and
                  verified by the onboarding team. Activation usually takes 1–2
                  business days.
                </p>
              </div>
            </div>
          </div>

          {/* SUBMIT LOADER */}
          {loading && (
            <div
              className="
                rounded-2xl
                border border-teal-500/20
                bg-teal-500/5
                p-4
                text-sm
                text-teal-300
                animate-pulse
              "
            >
              Processing your company setup...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StepReviewSummary;

// =====================================================
// INFO CARD
// =====================================================

const InfoCard = ({ icon: Icon, label, value }) => {
  return (
    <div
      className="
                rounded-2xl
                border border-slate-200 dark:border-slate-800
                bg-slate-50 dark:bg-slate-800/40
                p-4
            "
    >
      <div className="flex items-center gap-3">
        <div
          className="
                        w-9 h-9 rounded-xl
                        flex items-center justify-center
                        bg-slate-200 dark:bg-slate-700
                    "
        >
          <Icon
            className="
                            w-4 h-4
                            text-slate-700 dark:text-slate-300
                        "
          />
        </div>

        <div className="min-w-0">
          <p
            className="
                            text-xs font-medium
                            text-slate-500 dark:text-slate-400
                        "
          >
            {label}
          </p>

          <p
            className="
                            text-sm font-semibold
                            text-slate-800 dark:text-white
                            truncate
                            mt-0.5
                        "
          >
            {value || "-"}
          </p>
        </div>
      </div>
    </div>
  );
};
